import { i18n } from "@/i18n";
import router from "@/router";
import axios, {AxiosHeaders, AxiosInterceptorManager, AxiosInterceptorOptions, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import { useToast } from "vue-toastification";

export type Response<Data> = Promise<AxiosResponse<Data>>

export type WorkspaceId = number;
export type JourneyId = string;
export type PersonId = string;
export type OrganizationId = string;
export type PromotionId = string;

export type Url = string;
export type Email = string;
export type DateString = string;
export type OnboardingTag = string; // Enum but only used on the my-omind.
export type Version = string;
export type VrPin = string;

export type Lang = 'fr' | 'en' | 'es';
export type WorkspacePersonRoleCategory = 'coachee' | 'staff';
export type PersonRole = 'admin' | 'coach' | 'extern' | 'coachee';
export type MeetingName = 'feta_action_plan_meeting';
export type JourneyName =
  | 'ascent_online'
  | 'ascent_vr'
  | 'bilan_vr'
  | 'bilan_vr_flash'
  | 'bilan_vr_quest'
  | 'bootcamp_online'
  | 'feta'
  | 'feta_meeting'
  | 'ignition_online'
  | 'ignition_vr'
  | 'leadership'
  | 'resilience'
  | 'tingari'
  | 'ai_research'
  | 'feta_ascent'
  | 'feta_meeting'
  | 'feta_meeting_level_2'
  | 'client_chrysoberyl'
  | 'client_tourmaline'
  | 'ai_adoption'
  | 'client_sapphire'
  | 'client_cacoxenite';
export type JourneyStepName =
  | 'threshold'
  | 'impact_threshold'
  | 'feta_action_plan'
  | 'elearning_program'
  | 'feta_behaviors_awareness'
  | 'igt'
  | 'skills_synthesis'
  | 'my_omind_invitation'
  | 'chrysoberyl_synthesis'
  | 'tourmaline_synthesis'
  | 'feta_awareness_offboarding'
  | 'feta_elearning' // TODO;
  | 'ai_adoption_synthesis'
  | 'sapphire_synthesis'
  | 'cacoxenite_synthesis'
export type JourneyStepId =
  | 'feta_launch_threshold'
  | 'feta_launch_onboarding'
  | 'feta_ascent_threshold'
  | 'feta_behaviors_awareness'
  | 'feta_action_plan'
  | 'elearning_program'
  | 'feta_impact_threshold'
  | 'synthesis'
  | 'experience_synthesis'
  | 'experience_recommendation'
  | 'feta_awareness_ai_debrief'
  | 'feta_elearning'
  | 'feta_ascent_offboarding'
  | 'my_omind_invitation'
  | 'hdf_upload'
  // Bootcamp online customization
  | 'experience_emo_subj_sensitivity'
  | 'experience_emoquotation'
  | 'experience_emo_subj_regulation'
  | 'experience_oddball'
  | 'experience_scs-sf'
  | 'experience_oth_ohls'
  | 'experience_cog_subj_focus'
  | 'experience_focus'
  | 'experience_pci-cog'
  | 'experience_cog_subj_risktaking'
  | 'experience_bart'
  | 'experience_ius'
  | 'experience_cog_subj_flexibility'
  | 'experience_cogstroop'
  | 'experience_lms'
  | 'experience_soc_subj_cooperation'
  | 'experience_ultimatum'
  | 'experience_pci-soc-ins'
  | 'experience_soc_subj_empathy'
  | 'experience_rme'
  | 'experience_cs-sf'
  | 'experience_pci-soc-emo'
  // Tingari customization
  | 'survey_mhc'
  | 'survey_ius'
  | 'test_oddball'
  | 'test_bart'
  | 'survey_bpnsfs'
  | 'survey_eqri'
  | 'survey_hexaco-pi-r'
  | 'survey_lsas'
  | 'survey_pswq'
  | 'survey_rse'
  | 'survey_sep-tic'
  | 'survey_stai-y'
  | 'survey_gse'
  | 'synthesis_trigger'
  | 'edit_synthesis'
  | 'tutorial'
  | 'phenomenology'
  | 'feta_meeting_onboarding'
  | 'feta_action_plan_threshold'
  | 'feta_awareness_offboarding'
  | 'sapphire_synthesis'
  | 'chrysoberyl_synthesis'
  | 'tourmaline_synthesis'
  | 'cacoxenite_synthesis'

export type JourneyStepType =
  | 'misc'
  | 'report'
  | 'report_interaction'
  | 'survey'
  | 'game'
  | 'vr_game'
  | 'api_action'
  | 'trigger'
  | 'api_action'
  | 'ai_debrief'
  | 'ai';
export type JourneyStepGroup =
  | 'experience'
  | 'impact';
export type ContactCategory =
  | "technical_issue"
  | "credit_or_plan_issue"
  | "request_analysis"
  | "request_analysis"
export type CreditMode =
  | "per_usage"
  | "subscription"

export interface GetPersonAlias {
  first_name: string;
  last_name: string;
  created_at: DateString;
  updated_at?: DateString;
}

export interface GetJourneyStepSchema {
  group: JourneyStepGroup;
  content: {
    name: JourneyStepName;
    type: JourneyStepType;
    version: Version;
  };
  dependencies: JourneyStepId[];
}

export interface GetJourneyStepProps {
  name: JourneyStepId;
  data: {
    meta_data: {
      started_at: DateString | null;
      ended_at: DateString | null;
      is_valid?: boolean;
      is_finished?: boolean;
      is_available: boolean;
    };
  };
}

export interface JourneySchema {
  version: Version;
  steps: Record<JourneyStepId, GetJourneyStepSchema>;
  steps_not_allowed_in_quetzal: JourneyStepId[];
  reports: Record<JourneyStepName, {
    step: JourneyStepId;
    version: Version;
    elements: JourneyStepId[];
  }>;
  conditional_steps: Record<JourneyStepId, Record<Lang, {
    name: JourneyStepName;
    type: JourneyStepType;
    version: Version;
  }>>;
  steps_relationship: Record<JourneyStepId, JourneyStepId>;
  deletion_guards: JourneyStepId[];
}

export interface GetRoleCoachee {
  person_role: "coachee";
  workspace_id: WorkspaceId;
  created_at: DateString;
  updated_at?: DateString;
  organization_pids: OrganizationId[];
  promotion_pids: PromotionId[];
  journeys?: GetPersonJourney.Result[];
  vr_pin: VrPin | null;
}

export interface GetRoleStaff {
  person_role: Exclude<PersonRole, "coachee">;
  workspace_id: WorkspaceId;
  created_at: DateString;
  updated_at?: DateString;
}

export namespace GetMe {
  export interface Result {
    public_id: PersonId;
    first_name: string;
    last_name: string;
    email: Email;
    lang?: Lang;
    is_verified: boolean;
    lang_was_picked: boolean;
    alias?: {
      first_name: string;
      last_name: string;
      created_at: DateString;
      updated_at?: DateString;
    };
    roles: (GetRoleCoachee | GetRoleStaff)[];
    onboarding_tag: OnboardingTag;
    onboarding_data: object | null; // Only used by the my-omind
  }
}
export namespace PatchMe {
  export interface Params {
    first_name?: string;
    last_name?: string;
    lang?: Lang;
    onboarding_data?: object | null; // Only used by the my-omind
  }

  export type Result = GetMe.Result;
}

export namespace PostWorkspaceMeeting {
  export interface Params {
    journeys_pid: JourneyId[];
    meeting_name: MeetingName;
    meeting_alias: null | string;
    meeting_coach?: {
      coach_pid: PersonId;
      link: Url;
    }[];
  }

  export type Result = string;
}

export namespace GetJourneyMeeting {
  export interface Coach {
    coach_meeting_profile_pid: string;
    coach_meeting_link: string;
    first_name: string;
    last_name: string;
    person_pid: string;
  }

  export interface Result {
    meeting_name: MeetingName;
    has_coach_selection: boolean;
    coach_selection: Coach[]
  }
}
export namespace DeleteJourneyMeeting {
  export type Result = string;
}

export namespace GetJourneyExtern {
  export interface Result {
    extern_public_id: PersonId;
  }
}
export namespace PutJourneyExtern {
  export interface Params {
    extern_public_id: PersonId;
  }

  export type Result = string;
}
export namespace DeleteJourneyExtern {
  export type Result = string;
}

export namespace PostLegalSignature {
  export interface Params {
    contract_id: number;
  }

  export type Result = string;
}

export namespace GetWorkspaceCoachee {
  export interface Result {
    public_id: JourneyId;
    email: string;
    lang?: Lang;
    is_verified: boolean;
    workspace_id: WorkspaceId;
    person_alias: GetPersonAlias;
    person_roles: (GetRoleCoachee | GetRoleStaff)[];
  }
}

export namespace GetWorkspaceCoachees {
  export interface Query {
    page: undefined | number;
    page_size: undefined | number;
    journey_name: JourneyName[];
    organization_pid: string[];
    promotion_pid: string[];
    search: string | undefined;
    sort_by: SortBy | undefined;
  }

  export type Result = {
    pagination: {
      page: number;
      per_page: number;
      total_items: number;
    };
    data: GetWorkspaceCoachee.Result[];
  };

  export type SortBy = 'name_asc' | 'name_desc' | 'creation_asc' | 'creation_desc';
}

export namespace GetWorkspaceCoach {
  export interface Result {
    public_id: JourneyId;
    person_alias: GetPersonAlias;
    person_roles: (GetRoleStaff)[];
  }
}

export namespace GetWorkspaceCoaches {
  export interface Query {
  }

  export type Result = GetWorkspaceCoach.Result[];
}

export namespace PostWorkspaceCoachees {
  export type CoacheesParam = {
    first_name: string;
    last_name: string;
    email: string;
    organization_pids?: OrganizationId[];
    promotion_pids?: PromotionId[];
  }[];

  export type JourneyParam = {
    name: "bootcamp_online";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
    alias?: string;
  } | {
    name: "tingari";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
  } | {
    name: Exclude<JourneyName, "bootcamp_online" | "tingari">;
  } | {
    name: 'feta';
    threshold_to_unlock?: JourneyStepId;
  } | {
    name: 'client_chrysoberyl';
    threshold_to_unlock?: JourneyStepId;
  } | {
    name: 'client_tourmaline'
  } | {
    name: 'feta_meeting';
    threshold_to_unlock?: JourneyStepId;
  };

  export interface Params {
    coachees: CoacheesParam;
    journey?: JourneyParam;
  }

  export type Result = GetWorkspaceCoachee.Result[];
}
export namespace PatchWorkspaceCoachee {
  export interface Params {
    first_name?: string;
    last_name?: string;
    email?: Email;
    organization_pids?: null | OrganizationId[];
    promotion_pids?: null | PromotionId[];
  }

  export interface Result {
    email?: Email;
    person_alias?: GetPersonAlias;
    person_roles?: (GetRoleCoachee | GetRoleStaff)[];
  }
}

export namespace GetWorkspaceExtern {
  export interface Result {
    public_id: PersonId;
    person_alias: GetPersonAlias,
    person_roles: (GetRoleCoachee | GetRoleStaff)[];
  }
}
export namespace GetWorkspaceExterns {
  export type Result = GetWorkspaceExtern.Result[]
}

export namespace GetWorkspaceStaff {
  export interface Result {
    public_id: PersonId;
    email: string;
    is_verified: boolean;
    person_alias: GetPersonAlias,
    person_roles: (GetRoleCoachee | GetRoleStaff)[];
  }
}
export namespace GetWorkspaceStaffs {
  export type Result = GetWorkspaceStaff.Result[]
}

export namespace PostWorkspaceStaff {
  export type Params = {
    first_name: string;
    last_name: string;
    email: string;
    roles: PersonRole[];
  };

  export type Result = GetWorkspaceStaff.Result;
}

export namespace PatchWorkspaceStaff {
  export interface Params {
    first_name?: string;
    last_name?: string;
    email?: Email;
    roles: PersonRole[];
  }

  export interface Result {
    email?: Email;
    person_alias?: {
      created_at: DateString;
      updated_at?: DateString;
      first_name: string;
      last_name: string;
    };
    person_roles?: Partial<Record<PersonRole, null | {
      person_id: PersonId;
      workspace_id: WorkspaceId;
      created_at: DateString;
      updated_at?: DateString;
      person_role: PersonRole;
      organization_pids?: OrganizationId[];
      promotion_pids?: PromotionId[];
    }>>;
  }
}

export namespace DeleteWorkspaceStaff {
  export type Result = string;
}

export namespace GetPersonJourney {
  export interface Result {
    public_id: JourneyId;
    // workspace_id: WorkspaceId;
    name: JourneyName;
    created_at: DateString;
    updated_at?: DateString;
    is_closed: boolean;
    journey_schema: JourneySchema;
    steps: GetJourneyStepProps[];
    extern_pid?: PersonId;
  }
}
export namespace GetPersonJourneys {
  export type Result = GetPersonJourney.Result[];
}
export namespace PostWorkspacePersonJourney {
  interface TingariParams {
    journey_name: "tingari";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
  }

  interface BootcampOnlineParams {
    journey_name: "bootcamp_online";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
    alias?: string;
  }

  interface StandardParams {
    journey_name: Exclude<JourneyName, "tingari" | "bootcamp_online">;
  }

  export type Params = TingariParams | BootcampOnlineParams | StandardParams;

  export type Result = GetPersonJourney.Result;
}
export namespace PostWorkspacePersonJourneys {
  export interface Params {  
    person_pids: PersonId[];
    journey: {
      name: JourneyName;
      threshold_to_unlock?: JourneyStepId;
      steps?: Record<JourneyStepId, { dependencies: JourneyStepId[]; }>;
      alias?: string;
    }
  }

  export type Result = GetPersonJourney.Result[];
}
export namespace DeleteWorkspacePersonRole {
  export type Result = string;
}

export namespace GetWorkspace {
  // Returns the workspace from the point of view of the current user.
  export interface Result {
    id: WorkspaceId;
    name: string;
    config: {
      allow_promotion: boolean;
    };
    allow_meeting_coach_selection: boolean;
    allow_automatic_email: boolean;
    roles: PersonRole[]; // user <-> workspace
  }

  export type ResultWithCredits = Result & {
    credit: Partial<Record<JourneyName, { unexpired_credit: number, assigned_credit: number, credit_expiration: any }>>;
    credit_mode: CreditMode;
  }
}
export namespace GetWorkspaces {
  export type Result = GetWorkspace.Result[];
}
export namespace GetWorkspaceCreditsOld {
  export type Result = Partial<Record<JourneyName, { total_credits: number, used_credits: number }>>;
}
export namespace GetWorkspaceCredits {
  export type Result = Partial<Record<JourneyName, { unexpired_credit: number, assigned_credit: number, credit_expiration: any }>>;
}

export namespace GetReportPDFUrl {
  export interface Query {
    report_variant: 'feta_launch_variant';
  }

  export interface Result {
    url: Url;
  }
}
export namespace GetJourneyStep {
  export interface Result {
    name: JourneyStepId;
    data: any; // TODO
  }
}
export namespace PutJourneyStep {
  export type Params = any; // TODO

  export type Result = GetJourneyStep.Result;
}
export namespace PatchJourneyStep {
  export interface Params {
    data: {
      meta_data: {
        is_available: boolean;
      };
    };
  }

  export type Result = GetJourneyStep.Result;
}

export namespace PatchJourneySchema {
  export interface Params {
    steps: JourneyStepName[];
  }

  export interface Result {
    journey_schema: JourneySchema;
  }
}
export namespace PatchJourneys {
  export interface Params {
    is_closed: true;
    journey_pids: JourneyId[];
  }

  export type Result = {
    is_closed: boolean;
    public_id: JourneyId;
    updated_at: DateString;
  }[]
}
export namespace DeleteJourneys {
  export interface Params {
    journey_pids: JourneyId[];
  }
  export type Result = string;
}

export namespace PatchPersonPassword {
  export type Params = {
    password: string;
    old_password: string;
  } | {
    password: string;
    token: string;
  };

  export type Result = string;
}
export namespace VerifyAccount {
  export interface Params {
    url_token: string;
  }

  export type Result = string;
}
export namespace VerifyAccountAgain {
  export interface Params {
    url_token: string;
  }

  export type Result = string;
}
export namespace GetSessionStatus {
  export interface Result {
    status: 'is_alive';
  }
}
export namespace PostPasswordReset {
  export interface Params {
    email: Email;
  }

  export type Result = string;
}
export namespace SignUp {
  export interface Params {
    email: string;
    password: string;
    accept_cgu: boolean;
    accept_legal_mentions: boolean;
    lang: Lang;
  }

  export type Result = string;
}
export namespace SignIn {
  export interface Params {
    username: string;
    password: string;
  }

  export type Result = string;
}
export namespace SignOut {
  export type Result = string;
}

export namespace GetWorkspaceOrganization {
  export interface Result {
    pid: OrganizationId;
    workspace_id: WorkspaceId;
    created_at: DateString;
    updated_at?: DateString;
    organization_name: string;
  }
}
export namespace GetWorkspaceOrganizations {
  export type Result = GetWorkspaceOrganization.Result[];
}
export namespace PostWorkspaceOrganization {
  export interface Params {
    organization_name: string;
  }

  export type Result = GetWorkspaceOrganization.Result;
}

export namespace GetWorkspacePromotion {
  export interface Result {
    pid: PromotionId;
    workspace_id: WorkspaceId;
    created_at: DateString;
    updated_at?: DateString;
    promotion_name: string;
  }
}
export namespace GetWorkspacePromotions {
  export type Result = GetWorkspacePromotion.Result[];
}
export namespace PostWorkspacePromotion {
  export interface Params {
    promotion_name: string;
  }

  export type Result = GetWorkspacePromotion.Result;
}

export namespace GetJourneyExternId {

  export interface Result {
    extern_public_id: PersonId;
  }
}
export namespace PutJourneyExternId {
  export interface Params {
    extern_public_id: PersonId;
  }

  export type Result = string;
}
export namespace DeleteJourneyExternId {
  export type Result = string;
}
export namespace ContactUs {
  export interface Params {
    category: ContactCategory;
    analysis_uuids?: string[];
    message: string;
  }

  export type Result = string;
}

export namespace PostDebriefHelper {
  export interface Params {
    context: string | null;
    language: Lang;
    hub_version: string;
  }

  export interface Result {
    data: {
      meta_data: any;
      raw_data: {
        ai_debrief: string;
        context: string | undefined
      };
    },
    name: string
  }
}

// Bootcamps
export interface BootcampDiscussion{
  authz_pin: string;
  is_test: boolean;
}

export interface Bootcamp {
  pid: string;
  bootcamp_name: string;
  created_at: DateString;
  bootcamp_datetime: DateString;
  ai_instruction: string;
  ai_bootcamp_discussions: BootcampDiscussion[];
  is_closed?: boolean;
}

export namespace GetBootcamps {
  export interface Params {
  }

  export type Result = Bootcamp[]
}

export namespace PostBootcamp {
  export interface Params {
    bootcamp_name: string;
    bootcamp_datetime?: DateString;
    ai_instruction: string;
  }

  export interface Result {

  }
}

export interface AnalysisGroup {
  pid: string;
  analysis_group_name: string;
  collective_report_specification: {
    journey_names: string[];
  }
  group_size: number;
  analyzed_journey_count: number;
  created_at: DateString;
  /* created_at: DateString;
  bootcamp_datetime: DateString;
  ai_instruction: string;
  ai_bootcamp_discussions: BootcampDiscussion[];
  is_closed?: boolean; */
}

export namespace GetAnalysisGroups {
  export interface Params {
  }

  export type Result = AnalysisGroup[]
}


export namespace Template {
  export interface Params {
  }

  export interface Result {
  }
}

function toQueryString(query: any): string {
  const parts: string[] = [];

  if (query === null || query === undefined) return '';

  for (const key in query) {
    const value = query[key];

    if (value === undefined) continue;

    const values: any[] = Array.isArray(value) ? value : [value];

    for (const item of values)
      if (typeof item === "number" || typeof item === "string" || typeof item === "boolean")
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
  }

  return parts.length ? `?${parts.join('&')}` : '';
}


export interface HttpClient {
  post(url: string, body?: any): Promise<any>;

  get(url: string, config?: AxiosRequestConfig): Promise<any>;

  put(url: string, body?: any): Promise<any>;

  patch(url: string, body?: any): Promise<any>;

  delete(url: string, body?: any): Promise<any>;

  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse>;
  }
}

export class SDK {
  constructor(private api: HttpClient) {
    api.interceptors.request.use(
      config => {
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    api.interceptors.response.use(
      response => {
        return response
      },
      error => {
        if(error?.response?.status){
          if (401 === error.response.status) {
            const toast = useToast()
            toast.error(i18n.global.t('errors.disconnected'))
            router.replace({name: "sign_in", query: {redirect: router.currentRoute.value.fullPath}})
          }
          else{
            return Promise.reject(error)
          }
        }
      }
    )
  }

  // AUTHENTICATION
  patchPersonPassword(params: PatchPersonPassword.Params): Response<PatchPersonPassword.Result> {
    return this.api.patch(`/person/password`, params);
  }

  verifyAccount(params: VerifyAccount.Params): Response<VerifyAccount.Result> {
    return this.api.post(`/verify`, params);
  }

  verifyAccountAgain(params: VerifyAccountAgain.Params): Response<VerifyAccountAgain.Result> {
    return this.api.post(`/new-verification`, params);
  }

  getSessionStatus(): Response<GetSessionStatus.Result> {
    return this.api.get(`/session-status`);
  }

  postPasswordReset(params: PostPasswordReset.Params): Response<PostPasswordReset.Result> {
    return this.api.post(`/password-reset`, params);
  }

  signUp(params: SignUp.Params): Response<SignUp.Result> {
    return this.api.post(`/signup`, params);
  }

  signIn(params: SignIn.Params): Response<SignIn.Result> {
    return this.api.post(`/login`, params);
  }

  signOut(): Response<SignOut.Result> {
    return this.api.post(`/logout`);
  }

  // LEGAL
  postLegalSignature(params: PostLegalSignature.Params): Response<PostLegalSignature.Result> {
    return this.api.post(`/legal-signature`, params);
  }

  // ME
  getMe(): Response<GetMe.Result> {
    return this.api.get(`/me`);
  }

  patchMe(params: PatchMe.Params): Response<PatchMe.Result> {
    return this.api.patch(`/me`, params);
  }

  getWorkspaces(): Response<GetWorkspaces.Result> {
    return this.api.get(`/person/me/workspaces`);
  }

  getWorkspace(workspaceId: WorkspaceId): Response<GetWorkspace.ResultWithCredits> {
    return this.api.get(`/workspace/${workspaceId}`)
  }

  getWorkspaceCredits(workspaceId: WorkspaceId): Response<GetWorkspaceCredits.Result> {
    return this.api.get(`/workspace/${workspaceId}/credits`)
  }

  // PERSON
  getWorkspaceCoachees(workspaceId: WorkspaceId, query: GetWorkspaceCoachees.Query, config?: AxiosRequestConfig): Response<GetWorkspaceCoachees.Result> {
    return this.api.get(`/workspace/${workspaceId}/coachees${toQueryString(query)}`, config);
  }

  getWorkspaceCoaches(workspaceId: WorkspaceId, query: GetWorkspaceCoaches.Query, config?: AxiosRequestConfig): Response<GetWorkspaceCoaches.Result> {
    return this.api.get(`/workspace/${workspaceId}/coaches${toQueryString(query)}`, config);
  }

  postWorkspaceCoachees(workspaceId: WorkspaceId, params: PostWorkspaceCoachees.Params): Response<PostWorkspaceCoachees.Result> {
    return this.api.post(`/workspace/${workspaceId}/coachees`, params);
  }

  patchWorkspaceCoachee(workspaceId: WorkspaceId, personId: PersonId, params: PatchWorkspaceCoachee.Params): Response<PatchWorkspaceCoachee.Result> {
    return this.api.patch(`/workspace/${workspaceId}/person/${personId}/role/coachee`, params);
  }

  /* patchWorkspaceStaff(workspaceId: WorkspaceId, personId: PersonId, params: PatchWorkspaceStaff.Params): Response<PatchWorkspaceStaff.Result> {
    return this.api.patch(`/workspace/${workspaceId}/person/${personId}/role/staff`, params);
  } */

  postWorkspacePersonJourneys(workspaceId: WorkspaceId, params: PostWorkspacePersonJourneys.Params): Response<PostWorkspacePersonJourneys.Result> {
    return this.api.post(`/workspace/${workspaceId}/journeys`, params);
  }

  deleteWorkspacePersonRole(workspaceId: WorkspaceId, personId: PersonId, role: WorkspacePersonRoleCategory): Response<DeleteWorkspacePersonRole.Result> {
    return this.api.delete(`/workspace/${workspaceId}/person/${personId}/role/${role}`);
  }

  // EXTERNS
  getWorkspaceExterns(workspaceId: WorkspaceId): Response<GetWorkspaceExterns.Result> {
    return this.api.get(`/workspace/${workspaceId}/externs`);
  }

  // STAFF
  getWorkspaceStaff(workspaceId: WorkspaceId): Response<GetWorkspaceStaffs.Result> {
    return this.api.get(`/workspace/${workspaceId}/staff`);
  }

  postWorkspaceStaff(workspaceId: WorkspaceId, params: PostWorkspaceStaff.Params): Response<PostWorkspaceStaff.Result> {
    return this.api.post(`/workspace/${workspaceId}/staff`, params);
  }

  patchWorkspaceStaff(workspaceId: WorkspaceId, personId: PersonId, params: PatchWorkspaceStaff.Params): Response<PatchWorkspaceStaff.Result> {
    return this.api.patch(`/workspace/${workspaceId}/staff/${personId}`, params);
  }

  deleteWorkspaceStaff(workspaceId: WorkspaceId, personId: PersonId): Response<DeleteWorkspaceStaff.Result> {
    return this.api.delete(`/workspace/${workspaceId}/staff/${personId}`);
  }

  // JOURNEY EXTERNS
  getJourneyExtern(journeyId: JourneyId): Response<GetJourneyExtern.Result> {
    return this.api.get(`/journey/${journeyId}/coach-extern`);
  }

  putJourneyExtern(journeyId: JourneyId, params: PutJourneyExtern.Params): Response<PutJourneyExtern.Result> {
    return this.api.put(`/journey/${journeyId}/coach-extern`, params);
  }

  deleteJourneyExtern(journeyId: JourneyId): Response<DeleteJourneyExtern.Result> {
    return this.api.delete(`/journey/${journeyId}/coach-extern`);
  }

  // MEETINGS
  postWorkspaceMeeting(workspaceId: WorkspaceId, params: PostWorkspaceMeeting.Params): Response<PostWorkspaceMeeting.Result> {
    return this.api.post(`/workspace/${workspaceId}/meeting`, params);
  }

  getJourneyMeeting(journeyId: JourneyId, meetingName: MeetingName): Response<GetJourneyMeeting.Result> {
    return this.api.get(`/journey/${journeyId}/meeting/${meetingName}`);
  }

  deleteJourneyMeeting(journeyId: JourneyId, meetingName: MeetingName): Response<DeleteJourneyMeeting.Result> {
    return this.api.delete(`/journey/${journeyId}/meeting/${meetingName}`);
  }

  // JOURNEY STEP
  getReportPDFUrl(journeyId: JourneyId, stepName: JourneyStepName, query?: GetReportPDFUrl.Query): Response<GetReportPDFUrl.Result> {
    return this.api.get(`/journey/${journeyId}/report/${stepName}/pdf-url${toQueryString(query)}`);
  }

  getJourneyStep(journeyId: JourneyId, stepId: JourneyStepId): Response<GetJourneyStep.Result> {
    return this.api.get(`/journey/${journeyId}/step/${stepId}`);
  }

  putJourneyStep(journeyId: JourneyId, stepId: JourneyStepId, params: PutJourneyStep.Params): Response<PutJourneyStep.Result> {
    return this.api.put(`/journey/${journeyId}/step/${stepId}`, params);
  }

  patchJourneyStep(journeyId: JourneyId, stepId: JourneyStepId, params: PatchJourneyStep.Params): Response<PatchJourneyStep.Result> {
    return this.api.patch(`/journey/${journeyId}/step/${stepId}`, params);
  }

  getWorkspaceOrganizations(workspaceId: WorkspaceId): Response<GetWorkspaceOrganizations.Result> {
    return this.api.get(`/workspace/${workspaceId}/organizations`);
  }

  postWorkspaceOrganization(workspaceId: WorkspaceId, data: PostWorkspaceOrganization.Params): Response<PostWorkspaceOrganization.Result> {
    return this.api.post(`/workspace/${workspaceId}/organization`, data);
  }

  getWorkspacePromotions(workspaceId: WorkspaceId): Response<GetWorkspacePromotions.Result> {
    return this.api.get(`/workspace/${workspaceId}/promotions`);
  }

  postWorkspacePromotion(workspaceId: WorkspaceId, data: PostWorkspacePromotion.Params): Response<PostWorkspacePromotion.Result> {
    return this.api.post(`/workspace/${workspaceId}/promotion`, data);
  }

  contactUs(params: ContactUs.Params): Response<ContactUs.Result> {
    return this.api.post(`/email-support`, params);
  }

  postDebriefingHelper(journeyId: JourneyId, data: PostDebriefHelper.Params, debug?: boolean): Response<PostDebriefHelper.Result>{
    let route = `/journey/${journeyId}/report_ai_debrief`
    if(debug)
      route += '?dev_mode=true'
    return this.api.post(route, data);
  }

  // JOURNEYS BATCH
  patchJourneys(workspaceId: WorkspaceId, params: PatchJourneys.Params): Response<PatchJourneys.Result> {
    return this.api.patch(`/workspace/${workspaceId}/journeys`, params)
  }

  deleteJourneys(workspaceId: WorkspaceId, params: DeleteJourneys.Params): Response<DeleteJourneys.Result> {
    return this.api.delete(`/workspace/${workspaceId}/journeys`, { data: params})
  }


  // Bootcamps
  getBootcamps(workspaceId: WorkspaceId): Response<GetBootcamps.Result> {
    return this.api.get(`/workspace/${workspaceId}/ai-bootcamps`)
  }

  postBootcamp(workspaceId: WorkspaceId, params: PostBootcamp.Params): Response<PostBootcamp.Result> {
    return this.api.post(`/workspace/${workspaceId}/ai-bootcamp`, params)
  }

  patchBootcamp(workspaceId: WorkspaceId, bootcampId: string, params: PostBootcamp.Params): Response<PostBootcamp.Result> {
    return this.api.patch(`/workspace/${workspaceId}/ai-bootcamp/${bootcampId}`, params)
  }

  patchCloseBootcamp(workspaceId: WorkspaceId, bootcampId: string, params: {is_closed: boolean}): Response<PostBootcamp.Result> {
    return this.api.patch(`/workspace/${workspaceId}/ai-bootcamp/${bootcampId}`, params)
  }

  deleteBootcamp(workspaceId: WorkspaceId, bootcampId: string): Response<any> {
    return this.api.delete(`/workspace/${workspaceId}/ai-bootcamp/${bootcampId}`)
  }

  postBootcampGroups(workspaceId: WorkspaceId, bootcampId: string, params: {discussion_count?: number; test_discussion_count?:number}): Response<any> {
    return this.api.post(`/workspace/${workspaceId}/ai-bootcamp/${bootcampId}/ai-bootcamp-discussions`, params)
  }

  deleteBootcampGroup(workspaceId: WorkspaceId, bootcampId: string, groupId: string): Response<any> {
    return this.api.delete(`/workspace/${workspaceId}/ai-bootcamp/${bootcampId}/ai-bootcamp-discussion/${groupId}`)
  }

  //Collective Tools
  getAnalysisGroups(workspaceId: WorkspaceId): Response<GetAnalysisGroups.Result> {
    return this.api.get(`/workspace/${workspaceId}/analysis-groups`)
  }

  getAnalysisExterns(groupId: string): Response<any> {
    return this.api.get(`/analysis-group/${groupId}/externs`)
  }

  patchAnalysisGroups(groupId: string, data: {externs_person_pid: string[]}): Response<GetAnalysisGroups.Result> {
    return this.api.patch(`/analysis-group/${groupId}`, data)
  }
}



export const sdk = new SDK(
  axios.create({
    withCredentials: true,
    baseURL: import.meta.env.VITE_API_URL,
    headers: new axios.AxiosHeaders({
      "Omind-Application": "hub.omind"
    })
  })
);

export default sdk
